import * as Services from '../../../services/index.js';
import * as dates from '@simplybenefits/sb-dates';
import { hasPersonalInfoChanges, returnClassID } from './parseEmployeeData.js';
import * as localizationMaps from '../../../localization/maps.js';

/**
 * Validates the form data for an employee update, performing asynchronous input validation
 * using services and checking for specific changes between changedData and initialData.
 * 
 * @param {Object} changedData - Updated employee data to validate
 * @param {Object} initialData - Initial employee data for comparison
 * @returns {Promise<Object|boolean>} If errors are found, returns an object with validation errors;
 *                                    otherwise, returns false indicating no errors.
 */
const validateForm = async (changedData, initialData, isEdit, isAdmin, classData) => {
  const errors = {}; // Object to store validation errors

  const {
    firstname,
    lastname,
    email,
    birthdate,
    className,
    divisionName,
    hireDate,
    gender,
    title,
    coverageType,
    province,
    earningsType,
    salary,
    hourlyWage,
    hoursPerWeek,
    waiveEligibilityPeriod,
    phone,
    maritlStat
  } = changedData;
  errors.firstnameError = await Services.validateInput('firstname', firstname);
  errors.lastnameError = await Services.validateInput('lastname', lastname);
  errors.emailError = await Services.validateInput('email', email);
  errors.phoneError = isEdit && isAdmin && phone ? await Services.validateInput('phone', phone) : false;
  errors.maritlStatError = isEdit && isAdmin && maritlStat ? await Services.validateInput('maritlStat', maritlStat) : false;
  errors.birthdateError = await Services.validateInput('birthdate', birthdate);
  errors.classNameError = await Services.validateInput('className', className);
  errors.divisionNameError = await Services.validateInput('divisionName', divisionName);
  errors.hireDateError = await Services.validateInput('hireDate', hireDate);
  errors.genderError = await Services.validateInput('gender', gender);
  errors.titleError = await Services.validateInput('title', title);
  errors.coverageTypeError = await Services.validateInput('coverageType', coverageType);
  errors.provinceError = await Services.validateInput('province', province);
  errors.earningsTypeError = await Services.validateInput('earningsType', earningsType);
  errors.salaryError = localizationMaps.toEnglish('earningsTypes', earningsType) === 'Salary' ? await Services.validateInput('salary', salary) : null;
  errors.hourlyWageError = localizationMaps.toEnglish('earningsTypes', earningsType) === 'Hourly' ? await Services.validateInput('hourlyWage', hourlyWage) : null;
  errors.hoursPerWeekError = await Services.validateInput('hoursPerWeek', hoursPerWeek);
  errors.waiveEligibilityPeriodError = await Services.validateInput('waiveEligibilityPeriod', waiveEligibilityPeriod);
  if (isEdit && isAdmin) {
    const changesInPersonalInfo = hasPersonalInfoChanges(changedData, initialData);
    const changesInClassOrHireDate = returnClassID(classData, className) !== initialData.classID || dates.getUTC(hireDate) !== initialData.hireDate;
    if (changesInPersonalInfo && changesInClassOrHireDate && waiveEligibilityPeriod) {
      errors.employeeDetailsPersonalDetailsError = true;
    }
  }

  // Return false if there are any errors, otherwise true
  const hasErrors = Object.values(errors).some(Boolean);
  return hasErrors ? errors : false;
};
export default validateForm;